import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useMediaQuery } from '@mui/material'
import { NotePencilIcon } from '@phosphor-icons/react'

import { BaseModal2 } from '@components/modals/BaseModal2'
import { emotionTheme } from '@styles/theme'
import { Input } from '@components/shared/Input'
import { ReturnModal } from '@components/modals/ReturnModal'
import { WarningIcon } from '@assets/svgs/WarningIcon'
import { unMaskCurrency } from '@utils/masks/unMaskCurrency'
import { getAccountCodeForDisplay } from '@pages/People/helpers/accountingAccountMapping'
import { getGroupIcon } from '../../../helpers/getGroupIcon'
import type { EditDetailedSummaryPeopleModalProps } from './types'
import { editDetailedSummaryPeopleModalSchema } from './form/validations'
import { initialFormValues } from './form/initialFormValues'
import type { EditDetailedSummaryFormProps } from './form/types'
import * as S from './styles'

export const EditDetailedSummaryPeopleModal = ({
  open,
  onClose,
  item,
  group,
  onSave,
  isLoading = false,
  canEdit = true
}: EditDetailedSummaryPeopleModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)
  const [openCancelModal, setOpenCancelModal] = useState(false)

  const methods = useForm<EditDetailedSummaryFormProps>({
    resolver: editDetailedSummaryPeopleModalSchema,
    defaultValues: initialFormValues
  })

  // Pré-popular formulário quando item muda
  useEffect(() => {
    if (!open || !item) {
      methods.reset()
      return
    }

    methods.reset({
      amount: Number.isFinite(item.amount) ? item.amount.toFixed(2) : ''
    })
  }, [open, item, methods])

  const onSubmit = async () => {
    if (!canEdit) return
    if (!item || !group) return

    const formData = methods.getValues()
    const amount = unMaskCurrency(formData.amount)

    if (item.isDiscount && amount > 0) {
      methods.setError('amount', {
        type: 'manual',
        message: 'Este item é um desconto. O valor não pode ser positivo.'
      })
      return
    }

    try {
      await onSave({
        item,
        amount
      })
      onClose(false)
    } catch (error) {
      console.error('Erro ao salvar:', error)
    }
  }

  if (!item || !group) return null

  const groupIcon = group.icon || getGroupIcon(group.code)

  return (
    <>
      <BaseModal2
        open={open}
        title={group.name}
        headerIcon={groupIcon}
        onClose={() => setOpenCancelModal(true)}
        width={isMobile ? 'calc(100% - 20px)' : 600}
        heightContainer="auto"
        maxHeightContainer={isMobile ? 'calc(100% - 20px)' : '95%'}
        footer={{
          cancelButtonText: 'Voltar',
          cancelButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          confirmButtonText: canEdit ? 'Confirmar' : undefined,
          confirmButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          onConfirm: canEdit ? () => methods.handleSubmit(onSubmit)() : undefined,
          loading: isLoading
        }}
      >
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <S.FormContainer>
              {/* Título com descrição e conta */}
              <S.TitleSection>
                <S.TitleBox>
                  <NotePencilIcon size={20} weight="bold" color="#2D3748" />
                  <S.TitleText>Editar {item.description}</S.TitleText>
                </S.TitleBox>
              </S.TitleSection>

              {/* Banner de aviso amarelo */}
              <S.WarningBanner>
                <WarningIcon />
                <S.WarningText>
                  O valor inserido na edição, será contemplado na prévia até atualização automática pelo sistema.
                </S.WarningText>
              </S.WarningBanner>

              {/* Layout lado a lado: descrição à esquerda, input de valor à direita */}
              <S.ContentGrid>
                <S.LeftContent>
                  <S.ItemDescription>{item.description}</S.ItemDescription>
                  <S.ItemAccountCode>{getAccountCodeForDisplay(item.description, item.accountCode)}</S.ItemAccountCode>
                </S.LeftContent>
                <Input
                  label="Valor (R$)"
                  name="amount"
                  placeholder="Insira o valor"
                  maskType="currency"
                  required
                  disabled={!canEdit}
                  onBlur={({ target }) =>
                    target.value && methods.setValue('amount', unMaskCurrency(target.value).toFixed(2))
                  }
                />
              </S.ContentGrid>
            </S.FormContainer>
          </form>
        </FormProvider>
      </BaseModal2>

      {/* Modal de alterações não salvas */}
      <ReturnModal
        open={openCancelModal}
        headerIcon={<WarningIcon />}
        headerTitle="Alterações não salvas"
        message="Ao confirmar, os dados inseridos não serão salvos."
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenCancelModal}
        onConfirm={() => {
          setOpenCancelModal(false)
          onClose(false)
        }}
      />
    </>
  )
}
